import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserClientContext } from '@/app/clientContext';

/**
 * Keeps `?client=` in sync with the active client context:
 *   - Any user that can "client switch" will land on a route with the active client code in the URL.
 *     - right now this is internal overstory users and multi-tenant external users
 *   - All other users have any `?client=` stripped, since they cannot
 *     impersonate and a stale param would only mislead.
 *   - A `?delivery=` determines its own client, so it owns the client param:
 *     `?client=` is forced to the resolved client (added if absent, corrected if
 *     it disagrees) rather than allowed to drive a switch.
 *
 * Pairs with `useSyncClientFromQueryParam` (URL → context).
 */
export const useSyncQueryParamFromClient = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isUserLoaded, canClientSwitch, context } = useUserClientContext();
  const clientCode = context.code;
  const urlClient = searchParams.get('client');
  const urlDelivery = searchParams.get('delivery');

  useEffect(() => {
    if (!isUserLoaded) return;

    if (!canClientSwitch) {
      if (!urlClient) return;
      const next = new URLSearchParams(searchParams);
      next.delete('client');
      setSearchParams(next, { replace: true });
      return;
    }

    if (!clientCode) return;

    // The delivery is authoritative for the client, so keep `?client=` pinned to
    // the resolved client rather than letting a stale value drive a switch.
    if (urlDelivery) {
      if (urlClient?.toUpperCase() === clientCode.toUpperCase()) return;
      const next = new URLSearchParams(searchParams);
      next.set('client', clientCode);
      setSearchParams(next, { replace: true });
      return;
    }

    if (urlClient) return;

    const next = new URLSearchParams(searchParams);
    next.set('client', clientCode);
    setSearchParams(next, { replace: true });
  }, [
    isUserLoaded,
    canClientSwitch,
    clientCode,
    urlClient,
    urlDelivery,
    searchParams,
    setSearchParams,
  ]);
};
